/**
 * Object-Activity Linking Panel — UI for manually mapping 3D objects to schedule activities.
 * Supports multi-select from tree, activity selection from dropdown, and Excel import/export.
 * User can select which property to use as mapping key (dbId, Name, External ID, or any nested property).
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Link2, Unlink, Search, Loader2, CheckSquare, Square, Download,
  Upload, FileSpreadsheet, Layers, Box, ChevronRight, ChevronDown,
  FolderOpen, FolderClosed, Key
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Compact tree node with checkbox for multi-select
function LinkTreeNode({ node, depth, expandedNodes, onToggle, checkedDbIds, onCheck }) {
  const isExpanded = expandedNodes.has(node.dbId);
  const hasChildren = node.children && node.children.length > 0;
  const isChecked = checkedDbIds.has(node.dbId);

  return (
    <div>
      <div
        className="flex items-center gap-0.5 text-xs py-0.5 hover:bg-accent rounded transition-colors"
        style={{ paddingLeft: `${depth * 12 + 2}px` }}
      >
        {hasChildren ? (
          <button onClick={() => onToggle(node.dbId)} className="shrink-0 w-4 h-4 flex items-center justify-center">
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-4" />}
        <button onClick={() => onCheck(node.dbId)} className="shrink-0 w-4 h-4 flex items-center justify-center" data-testid={`link-check-${node.dbId}`}>
          {isChecked ? <CheckSquare className="w-3.5 h-3.5 text-blue-400" /> : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
        </button>
        {hasChildren
          ? (isExpanded ? <FolderOpen className="w-3 h-3 text-amber-500 shrink-0" /> : <FolderClosed className="w-3 h-3 text-amber-500/70 shrink-0" />)
          : <Box className="w-3 h-3 text-slate-500 shrink-0" />
        }
        <span className="truncate ml-0.5 cursor-pointer" onClick={() => onCheck(node.dbId)}>{node.name || `#${node.dbId}`}</span>
      </div>
      {isExpanded && hasChildren && node.children.map(c => (
        <LinkTreeNode key={c.dbId} node={c} depth={depth + 1} expandedNodes={expandedNodes} onToggle={onToggle} checkedDbIds={checkedDbIds} onCheck={onCheck} />
      ))}
    </div>
  );
}

export function LinkingPanel({
  modelId, mappings, objectTreeRoots, flatObjectTreeLength,
  expandedTreeNodes, onToggle, onRefreshMappings, projectId
}) {
  const PROJECT_ID = projectId;
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [checkedDbIds, setCheckedDbIds] = useState(new Set());
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkingAll, setUnlinkingAll] = useState(false);
  const [treeSearch, setTreeSearch] = useState('');
  const [propertyKey, setPropertyKey] = useState('dbId');
  const [propertyKeys, setPropertyKeys] = useState({ baseKeys: [], propertyKeys: [] });
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [importingMapping, setImportingMapping] = useState(false);
  const fileRef = useRef(null);

  const selectedMapping = mappings.find(m => m.activityId === selectedActivityId);
  const linkedObjects = selectedMapping?.modelObjects || [];
  const leafActivities = mappings.filter(m => !m.isSummary && m.activityType !== 'SUMMARY');

  // Fetch available property keys when modelId changes
  useEffect(() => {
    if (!modelId) return;
    setLoadingKeys(true);
    authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/object-tree/property-keys`)
      .then(r => r.json())
      .then(data => setPropertyKeys(data))
      .catch(() => {})
      .finally(() => setLoadingKeys(false));
  }, [modelId]);

  const handleCheck = useCallback((dbId) => {
    setCheckedDbIds(prev => {
      const next = new Set(prev);
      if (next.has(dbId)) next.delete(dbId); else next.add(dbId);
      return next;
    });
  }, []);

  const handleLinkObjects = async () => {
    if (!modelId || !selectedActivityId || checkedDbIds.size === 0) {
      toast.error('Select an activity and at least one object');
      return;
    }
    setLinking(true);
    try {
      const existingIds = linkedObjects.map(o => o.dbId);
      const allIds = [...new Set([...existingIds, ...checkedDbIds])];
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/mappings/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activityId: selectedActivityId, dbIds: allIds }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Linked ${checkedDbIds.size} objects (total: ${allIds.length})`);
        setCheckedDbIds(new Set());
        onRefreshMappings();
      } else {
        toast.error(d.detail || 'Link failed');
      }
    } catch (err) { toast.error(err.message); }
    setLinking(false);
  };

  const handleUnlinkObject = async (dbId) => {
    if (!modelId || !selectedActivityId) return;
    setUnlinking(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/mappings/${selectedActivityId}/objects/${dbId}`, {
        method: 'DELETE',
      });
      if (r.ok) {
        toast.success('Object unlinked');
        onRefreshMappings();
      } else {
        toast.error('Unlink failed');
      }
    } catch { toast.error('Unlink failed'); }
    setUnlinking(false);
  };

  const handleUnlinkAll = async () => {
    if (!modelId) return;
    const totalLinked = mappings.reduce((sum, m) => sum + (m.modelObjects?.length || 0), 0);
    if (totalLinked === 0) {
      toast.info('No objects are currently linked');
      return;
    }
    if (!window.confirm(`Unlink ALL ${totalLinked} model objects from ALL activities? This cannot be undone.`)) return;
    setUnlinkingAll(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/mappings/unlink-all`, { method: 'DELETE' });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Unlinked all objects from ${d.activitiesCleared} activities`);
        onRefreshMappings();
      } else {
        toast.error(d.detail || 'Unlink all failed');
      }
    } catch (err) { toast.error(err.message); }
    setUnlinkingAll(false);
  };

  // Template download with selected property key
  const handleTemplate = () => {
    if (!modelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/mappings/template?property_key=${encodeURIComponent(propertyKey)}`;
    a.download = '4D_Template.xlsx';
    a.click();
    toast.success(`Template download started (key: ${propertyKey})`);
  };

  // Export with selected property key
  const handleExport = () => {
    if (!modelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/mappings/export?property_key=${encodeURIComponent(propertyKey)}`;
    a.download = '4D_Mappings.xlsx';
    a.click();
    toast.success(`Export download started (key: ${propertyKey})`);
  };

  // Import with selected property key
  const handleImport = async (file) => {
    if (!modelId || !file) return;
    setImportingMapping(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('property_key', propertyKey);
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/mappings/import`, { method: 'POST', body: form });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Imported: ${d.updated} updated (key: ${propertyKey})`);
        if (d.errors?.length > 0) toast.warning(`${d.errors.length} warnings`);
        onRefreshMappings();
      } else {
        toast.error(d.detail || 'Import failed');
      }
    } catch (err) { toast.error(err.message); }
    setImportingMapping(false);
  };

  // Filter tree nodes by search
  const filterTree = (nodes, term) => {
    if (!term) return nodes;
    const lc = term.toLowerCase();
    return nodes.reduce((acc, node) => {
      const match = (node.name || '').toLowerCase().includes(lc);
      const filteredChildren = node.children ? filterTree(node.children, term) : [];
      if (match || filteredChildren.length > 0) {
        acc.push({ ...node, children: match ? node.children : filteredChildren });
      }
      return acc;
    }, []);
  };

  const visibleRoots = filterTree(objectTreeRoots, treeSearch);

  // All property options for dropdown
  const allPropertyOptions = [
    ...(propertyKeys.baseKeys || []).map(k => ({ value: k.key, label: k.label })),
    ...(propertyKeys.propertyKeys || []).map(k => ({ value: k, label: k })),
  ];

  return (
    <div className="flex flex-col h-full" data-testid="linking-panel">
      {/* Row 1: Activity selector + Link button */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b shrink-0">
        <Layers className="w-3 h-3 text-muted-foreground shrink-0" />
        <Select value={selectedActivityId} onValueChange={setSelectedActivityId}>
          <SelectTrigger className="h-7 flex-1 min-w-[180px] text-xs" data-testid="link-activity-select">
            <SelectValue placeholder="Select activity to link..." />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {leafActivities.map(a => (
              <SelectItem key={a.activityId} value={a.activityId}>
                <span className="font-mono text-[10px] text-muted-foreground mr-1">{a.activityCode}</span>
                {a.activityName}
                {a.modelObjects?.length > 0 && <Badge variant="secondary" className="ml-1 text-[9px] h-4 px-1">{a.modelObjects.length} obj</Badge>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="default" size="sm" className="h-7 text-xs gap-1" onClick={handleLinkObjects} disabled={linking || !selectedActivityId || checkedDbIds.size === 0} data-testid="link-objects-btn">
          {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
          Link ({checkedDbIds.size})
        </Button>
      </div>

      {/* Row 2: Property key selector + Template/Export/Import + Unlink All */}
      <div className="flex items-center gap-1.5 px-2 py-1 border-b shrink-0 bg-muted/30">
        <Key className="w-3 h-3 text-muted-foreground shrink-0" />
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">Map by:</span>
        <Select value={propertyKey} onValueChange={setPropertyKey}>
          <SelectTrigger className="h-6 min-w-[140px] max-w-[260px] text-[11px] bg-background" data-testid="property-key-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {loadingKeys ? (
              <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" />Loading...</div>
            ) : allPropertyOptions.length === 0 ? (
              <div className="px-2 py-1 text-xs text-muted-foreground">No properties available</div>
            ) : (
              allPropertyOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={handleTemplate} title={`Download template (by ${propertyKey})`} data-testid="link-template-btn">
          <FileSpreadsheet className="w-3 h-3" />Tmpl
        </Button>
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={handleExport} title={`Export mappings (by ${propertyKey})`} data-testid="link-export-btn">
          <Download className="w-3 h-3" />Export
        </Button>
        <input type="file" accept=".xlsx,.xls" className="hidden" ref={fileRef}
          onChange={e => { handleImport(e.target.files?.[0]); e.target.value = ''; }} />
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={() => fileRef.current?.click()} disabled={importingMapping} title={`Import mappings (by ${propertyKey})`} data-testid="link-import-btn">
          {importingMapping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Import
        </Button>
        <div className="w-px h-4 bg-border mx-0.5" />
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 gap-0.5 text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={handleUnlinkAll} disabled={unlinkingAll} title="Unlink all objects from all activities" data-testid="unlink-all-btn">
          {unlinkingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}Unlink All
        </Button>
      </div>

      {/* Main content: object tree (left) + linked objects (right) */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Object tree with checkboxes */}
        <div className="flex flex-col flex-1 min-w-0 border-r">
          <div className="flex items-center gap-1 px-2 py-1 border-b">
            <Search className="w-3 h-3 text-muted-foreground" />
            <Input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Filter objects..." className="h-6 text-xs border-0 shadow-none focus-visible:ring-0" data-testid="link-tree-search" />
            {checkedDbIds.size > 0 && (
              <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1" onClick={() => setCheckedDbIds(new Set())}>
                Clear
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-auto px-1 py-0.5" data-testid="link-object-tree">
            {flatObjectTreeLength === 0 ? (
              <div className="text-xs text-muted-foreground text-center pt-6">
                <Box className="w-6 h-6 mx-auto text-muted-foreground/30 mb-1" />
                No objects. Load object tree first.
              </div>
            ) : (
              visibleRoots.map(root => (
                <LinkTreeNode
                  key={root.dbId} node={root} depth={0}
                  expandedNodes={expandedTreeNodes} onToggle={onToggle}
                  checkedDbIds={checkedDbIds} onCheck={handleCheck}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: Currently linked objects for selected activity */}
        <div className="w-[260px] min-w-[200px] shrink-0 flex flex-col overflow-hidden">
          <div className="px-2 py-1.5 border-b text-xs font-semibold text-muted-foreground">
            Linked Objects {linkedObjects.length > 0 && `(${linkedObjects.length})`}
          </div>
          <div className="flex-1 overflow-auto px-2 py-1">
            {!selectedActivityId ? (
              <div className="text-[11px] text-muted-foreground text-center pt-4">Select an activity to see linked objects</div>
            ) : linkedObjects.length === 0 ? (
              <div className="text-[11px] text-muted-foreground text-center pt-4">No objects linked yet. Select objects from the tree and click Link.</div>
            ) : (
              linkedObjects.map(obj => (
                <div key={obj.dbId} className="flex items-center gap-1 py-0.5 text-[11px] group">
                  <Box className="w-3 h-3 text-blue-400 shrink-0" />
                  <span className="font-mono text-muted-foreground">#{obj.dbId}</span>
                  <span className="truncate flex-1">{obj.objectName || ''}</span>
                  <button
                    onClick={() => handleUnlinkObject(obj.dbId)}
                    disabled={unlinking}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                    data-testid={`unlink-${obj.dbId}`}
                  >
                    <Unlink className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
